import { Button, Input } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { type FC } from 'react';

import { CredentialPermissionsEditor } from '../credentials/credential-permissions-editor';
import {
  SettingsActionRow,
  SettingsActionsSection,
  SettingsCodeBlock,
  SettingsConfirmButton,
  SettingsContentSection,
  SettingsContentSurface,
  SettingsContentText,
  SettingsNotice,
} from '../shared/settings-content';
import * as formStyles from '../shared/settings-form.css';
import type { WebhookCredential } from './webhook-types';

export const WebhookManageTab: FC<{
  webhook: WebhookCredential;
  canManageEndpoints: boolean;
  draftName: string;
  renaming: boolean;
  rotating: boolean;
  deleting: boolean;
  setDraftName: (value: string) => void;
  setRenaming: (value: boolean) => void;
  onRename: () => void;
  onRotate: () => void;
  onDelete: () => void;
  canRotateToken: boolean;
}> = ({
  webhook,
  canManageEndpoints,
  draftName,
  renaming,
  rotating,
  deleting,
  setDraftName,
  setRenaming,
  onRename,
  onRotate,
  onDelete,
  canRotateToken,
}) => {
  const t = useI18n();

  if (!canManageEndpoints) return null;

  return (
    <SettingsContentSurface>
      {canRotateToken ? (
        <SettingsContentSection
          title={
            t[
              'com.streamlain.settings.workspace.webhooks.card.management'
            ]?.() ?? 'Manage endpoint'
          }
        >
          <div className={formStyles.formRow}>
            <Input
              value={draftName}
              onChange={setDraftName}
              disabled={!renaming}
              style={{ flex: 1, minWidth: '220px' }}
              onClick={e => e.stopPropagation()}
              spellCheck={false}
            />
            {renaming ? (
              <>
                <Button
                  variant="primary"
                  onClick={e => {
                    e.stopPropagation();
                    onRename();
                  }}
                >
                  {t['Save']?.() ?? 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={e => {
                    e.stopPropagation();
                    setDraftName(webhook.name);
                    setRenaming(false);
                  }}
                >
                  {t['Cancel']?.() ?? 'Cancel'}
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                onClick={e => {
                  e.stopPropagation();
                  setRenaming(true);
                }}
              >
                {t[
                  'com.streamlain.settings.workspace.automations.webhook.rename'
                ]?.() ?? 'Rename'}
              </Button>
            )}
          </div>
        </SettingsContentSection>
      ) : null}

      <SettingsActionsSection
        title={
          t['com.streamlain.settings.workspace.webhooks.card.actions']?.() ??
          'Sensitive actions'
        }
      >
        {canRotateToken ? (
          <Button variant="secondary" onClick={onRotate} disabled={rotating}>
            {rotating
              ? (t[
                  'com.streamlain.settings.workspace.automations.webhook.rotating'
                ]?.() ?? 'Rotating...')
              : (t[
                  'com.streamlain.settings.workspace.automations.webhook.rotate'
                ]?.() ?? 'Rotate token')}
          </Button>
        ) : null}

        <SettingsConfirmButton
          label={
            t[
              'com.streamlain.settings.workspace.automations.webhook.delete'
            ]?.() ?? 'Delete webhook'
          }
          confirmLabel={
            t[
              'com.streamlain.settings.workspace.automations.webhook.confirm-delete'
            ]?.() ?? 'Confirm delete'
          }
          cancelLabel={t['Cancel']?.() ?? 'Cancel'}
          loading={deleting}
          loadingLabel={
            t[
              'com.streamlain.settings.workspace.automations.webhook.deleting'
            ]?.() ?? 'Deleting...'
          }
          onConfirm={onDelete}
        />
      </SettingsActionsSection>
    </SettingsContentSurface>
  );
};

export const WebhookSecretTab: FC<{
  resolvedUrl: string | null;
  copied: boolean;
  onCopy: () => void;
}> = ({ resolvedUrl, copied, onCopy }) => {
  const t = useI18n();
  const safeResolvedUrl = resolvedUrl ?? '';

  return (
    <SettingsContentSurface>
      {!resolvedUrl ? (
        <SettingsNotice
          title={t[
            'com.streamlain.settings.workspace.webhooks.secret.owner-only-title'
          ]()}
        >
          {t['com.streamlain.settings.workspace.webhooks.secret.owner-only']()}
        </SettingsNotice>
      ) : null}
      {resolvedUrl ? (
        <>
          <SettingsContentText>
            {t[
              'com.streamlain.settings.workspace.automations.webhook.url-desc'
            ]?.() ??
              'Send POST requests to this URL to trigger the automation.'}
          </SettingsContentText>
          <SettingsCodeBlock>{safeResolvedUrl}</SettingsCodeBlock>
          <SettingsActionRow>
            <Button variant="secondary" onClick={onCopy}>
              {copied
                ? (t['com.streamlain.settings.workspace.webhooks.copied']?.() ??
                  'Copied')
                : (t['com.streamlain.settings.workspace.webhooks.copy']?.() ??
                  'Copy URL')}
            </Button>
          </SettingsActionRow>
          <SettingsContentText>
            {t[
              'com.streamlain.settings.workspace.automations.webhook.curl-desc'
            ]?.() ?? 'Example request for testing this webhook endpoint.'}
          </SettingsContentText>
          <SettingsCodeBlock as="pre">{`curl -X POST ${safeResolvedUrl} \\
  -H "Content-Type: application/json" \\
  -d '{"key":"value"}'`}</SettingsCodeBlock>
        </>
      ) : null}
    </SettingsContentSurface>
  );
};

export const WebhookAccessTab: FC<{
  webhookId: string;
  hasAccessControl: boolean;
  canManageAccess: boolean;
}> = ({ webhookId, hasAccessControl, canManageAccess }) => {
  const t = useI18n();

  return hasAccessControl ? (
    canManageAccess ? (
      <CredentialPermissionsEditor credentialId={webhookId} />
    ) : (
      <SettingsContentSurface>
        <SettingsNotice
          title={
            t[
              'com.streamlain.settings.workspace.webhooks.permissions.admin-only-title'
            ]?.() ?? 'Admin access required'
          }
        >
          {t[
            'com.streamlain.settings.workspace.webhooks.permissions.admin-only'
          ]?.() ??
            'Only workspace owners and admins can manage webhook permissions.'}
        </SettingsNotice>
      </SettingsContentSurface>
    )
  ) : (
    <SettingsContentSurface>
      <SettingsNotice
        title={
          t[
            'com.streamlain.settings.workspace.webhooks.permissions.upgrade-title'
          ]?.() ?? 'RBAC available on Team and above'
        }
      >
        {t[
          'com.streamlain.settings.workspace.webhooks.permissions.upgrade-desc'
        ]?.() ??
          'This workspace can create and manage webhook endpoints, but per-endpoint role assignments are available on Team, Enterprise, Beta, and Internal plans.'}
      </SettingsNotice>
    </SettingsContentSurface>
  );
};
